<template>
  <div class="merge-items-container merge-items-main merge-items-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Merge Items']" />

    <form class="merge-items-form" @submit.prevent="handleMerge">
      <div class="glass-tab-view glass-tab-view--flush merge-items-glass-shell">
        <div class="glass-tab-view__content merge-items-page-stack">
          <div class="merge-items-header-block">
            <h2 class="merge-items-title">Merge Items</h2>
            <p class="merge-items-subtitle">
              Combine duplicate inventory or service items by moving activity from one item into another
            </p>
          </div>

          <div class="merge-items-comparison">
            <div class="merge-items-section merge-items-section--from glass-tab-panel--nested">
              <h3 class="merge-items-section-title">Item From</h3>
              <p class="merge-items-section-hint">Source item to merge away</p>
              <div class="merge-items-form-row merge-items-form-row--stack">
                <div class="merge-items-form-group merge-items-form-group--dropdown">
                  <LabelDropdown
                    label="Item"
                    select-id="merge-items-from"
                    v-model="selectedFrom"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search items..."
                    :options="itemSelectOptions"
                  />
                </div>
                <div class="merge-items-form-group">
                  <LabelInputField
                    input-id="merge-items-from-type"
                    label="Item Type"
                    :model-value="fromItem?.type || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-items-form-group merge-items-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-items-from-active"
                    label="Active"
                    :model-value="!!fromItem?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-items-form-group merge-items-form-group--amount"
                  :class="{ 'merge-items-form-group--amount-empty': !fromItem?.salesPrice }"
                >
                  <LabelInputField
                    input-id="merge-items-from-sales-price"
                    label="Sales Price"
                    :model-value="fromItem?.salesPrice || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>

            <div class="merge-items-comparison-arrow" aria-hidden="true">
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="merge-items-section merge-items-section--to glass-tab-panel--nested">
              <h3 class="merge-items-section-title">Item To</h3>
              <p class="merge-items-section-hint">Target item to keep</p>
              <div class="merge-items-form-row merge-items-form-row--stack">
                <div class="merge-items-form-group merge-items-form-group--dropdown">
                  <LabelDropdown
                    label="Item"
                    select-id="merge-items-to"
                    v-model="selectedTo"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search items..."
                    :options="itemSelectOptions"
                  />
                </div>
                <div class="merge-items-form-group">
                  <LabelInputField
                    input-id="merge-items-to-type"
                    label="Item Type"
                    :model-value="toItem?.type || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-items-form-group merge-items-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-items-to-active"
                    label="Active"
                    :model-value="!!toItem?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-items-form-group merge-items-form-group--amount"
                  :class="{ 'merge-items-form-group--amount-empty': !toItem?.salesPrice }"
                >
                  <LabelInputField
                    input-id="merge-items-to-sales-price"
                    label="Sales Price"
                    :model-value="toItem?.salesPrice || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="merge-items-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" @click="handleCancel" />
            <PrimaryButton
              label="Merge"
              icon="bi bi-arrow-left-right"
              @click="handleMerge"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import '../styles/MergeItems.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'MergeItems',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    LabelCheckboxField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      items: [
        { id: 'I-001', name: 'Laptop', type: 'Electronics', active: true, salesPrice: '$1,200.00' },
        { id: 'I-002', name: 'Office Desk', type: 'Furniture', active: true, salesPrice: '$350.00' },
        { id: 'I-003', name: 'Printer', type: 'Electronics', active: false, salesPrice: '$220.00' },
        { id: 'I-004', name: 'Projector', type: 'Electronics', active: true, salesPrice: '$500.00' },
      ],
      selectedFrom: '',
      selectedTo: '',
    };
  },
  computed: {
    itemSelectOptions() {
      return this.items.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    },
    fromItem() {
      return this.items.find((i) => i.id === this.selectedFrom);
    },
    toItem() {
      return this.items.find((i) => i.id === this.selectedTo);
    },
  },
  methods: {
    handleMerge() {
      if (!this.selectedFrom || !this.selectedTo) {
        this.$toast && this.$toast.error('Please select both Item From and Item To.');
        return;
      }
      if (this.selectedFrom === this.selectedTo) {
        this.$toast && this.$toast.error('Item From and Item To must be different.');
        return;
      }
      this.$toast && this.$toast.info('Merge items coming soon!');
    },
    handleCancel() {
      if (this.$router) {
        this.$router.go(-1);
        return;
      }
      this.selectedFrom = '';
      this.selectedTo = '';
    },
  },
};
</script>
